/**
 * Service de gestion des statistiques
 * Centralise tous les appels API liés aux statistiques et rapports
 */
import apiService from './api.service';

class StatisticsService {
  /**
   * Obtenir les statistiques de réservations
   * Note: L'API utilise POST au lieu de GET pour cette récupération
   * @param {string} period - 'jour', 'semaine', 'mois', 'annuel', 'periodique', 'date'
   * @param {string} section - 'compagnie', 'gare', ''
   * @param {object} data - Données de filtre
   */
  async getReservationStatistics(period, section, data = {}) {
    const response = await apiService.postForGet(
      `statistiques/reservation/${period}/${section}`,
      { data }
    );
    return response;
  }

  /**
   * Obtenir les statistiques des bagages
   * @param {string} period - 'jour', 'semaine', 'mois', 'annuel'
   */
  async getLuggageStatistics(period, data = {}) {
    const response = await apiService.postForGet(
      `statistiques/bagage/${period}/gare`,
      { data }
    );
    return response;
  }

  /**
   * Obtenir les statistiques d'une offre de voyage
   * @param {string} period - 'jour', 'semaine', 'mois', 'annuel'
   * @param {object} data - Données incluant offreVoyageDesignation
   */
  async getOfferStatistics(period, data = {}) {
    const response = await apiService.postForGet(
      `statistiques/offreVoyage/${period}/compagnie`,
      { data }
    );
    return response;
  }

  /**
   * Obtenir les statistiques de revenus
   * @param {string} period - Période de statistiques
   * @param {object} data - Données de filtre
   */
  async getRevenueStatistics(period, data = {}) {
    return await this.getReservationStatistics(period, 'compagnie', data);
  }

  /**
   * Obtenir les statistiques par gare
   * @param {string} period - Période de statistiques
   * @param {object} data - Données de filtre incluant gareDesignation
   */
  async getStationStatistics(period, data = {}) {
    return await this.getReservationStatistics(period, 'gare', data);
  }

  /**
   * Obtenir les statistiques du jour
   * @param {object} data - Données de filtre
   */
  async getTodayStatistics(data = {}) {
    return await this.getReservationStatistics('jour', 'compagnie', data);
  }

  /**
   * Obtenir les statistiques de la semaine
   * @param {object} data - Données de filtre
   */
  async getWeekStatistics(data = {}) {
    return await this.getReservationStatistics('semaine', 'compagnie', data);
  }

  /**
   * Obtenir les statistiques du mois
   * @param {object} data - Données de filtre
   */
  async getMonthStatistics(data = {}) {
    return await this.getReservationStatistics('mois', 'compagnie', data);
  }

  /**
   * Obtenir les statistiques de l'année
   * @param {object} data - Données de filtre
   */
  async getYearStatistics(data = {}) {
    return await this.getReservationStatistics('annuel', 'compagnie', data);
  }

  /**
   * Obtenir les statistiques pour une période personnalisée
   * @param {object} data - Données incluant dateDebut et dateFin
   */
  async getCustomPeriodStatistics(data = {}) {
    return await this.getReservationStatistics('periodique', 'compagnie', data);
  }

  /**
   * Obtenir les statistiques pour une date spécifique
   * @param {object} data - Données incluant date
   */
  async getSpecificDateStatistics(data = {}) {
    return await this.getReservationStatistics('date', 'compagnie', data);
  }
}

export default new StatisticsService();
